import React, { useState, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { Combobox } from 'modules/ui/molecules/Combobox'
import useOrder from 'modules/order/state/order/use-order'
import useEmployerMatchingSearch, {
  EmployerMatchingProvider,
} from 'modules/order/components/employer-matching/use-employer-matching-search'
import useConfig from 'modules/config/state/use-config'
import mParticleSdk from '@mparticle/web-sdk'
import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'

const EmployerMatching = () => {
  const { t } = useTranslation()
  const trackUIElementEvent = useTrackUIElementEvent()
  const order = useOrder()
  const config = useConfig()

  const [searchQuery, setSearchQuery] = useState('')
  const hasInteracted = useRef(false)

  // Determine which provider to use based on organization settings
  // Amply integration is indicated by has_employer_match
  // Double the Donation integration is indicated by has_double_the_donation_employer_match
  const provider = config.doubleTheDonation.isEnabled ? 'DOUBLE_THE_DONATION' : 'AMPLY'

  const { data, companies, loading, error } = useEmployerMatchingSearch({
    provider,
    query: searchQuery,
    enabled: true,
  })

  const storedCompanyId = order.doubleTheDonation.value?.companyId
  const selectedLabel = order.doubleTheDonation.value?.companyName

  const selectedValue =
    storedCompanyId != null
      ? provider === 'AMPLY'
        ? `amply_${storedCompanyId}`
        : storedCompanyId
      : undefined

  const handleSelect = (value: string | number, label: string) => {
    // Track the selection
    trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
      tracking_id: 'employer matching selection',
      value: label,
      provider: provider,
    })

    // Convert value to number to match expected type
    // For Amply providers, extract numeric part from string like "amply_0"
    let numericId: number

    if (typeof value === 'string' && value.startsWith('amply_')) {
      // Extract number from "amply_0" -> 0
      numericId = parseInt(value.split('_')[1], 10) || 0
    } else {
      numericId = typeof value === 'number' ? value : parseInt(String(value), 10) || 0
    }

    // Set the selection using numeric ID and provided label
    order.doubleTheDonation.setValue({
      companyId: numericId,
      companyName: label,
    })
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)

    // Track first interaction with employer matching
    if (!hasInteracted.current && query.length > 0) {
      hasInteracted.current = true

      trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
        tracking_id: 'employer matching start',
        value: 'enabled',
        provider: provider,
      })
    }
  }

  const handleDropdownToggle = (isOpen: boolean) => {
    trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
      tracking_id: 'employer matching toggle',
      value: isOpen ? 'opened' : 'closed',
      provider: provider,
    })
  }

  const handleClear = () => {
    // Track employer matching clear event
    trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
      tracking_id: 'employer matching clear',
      value: 'selection cleared',
      provider: provider,
    })

    // Clear the selection
    order.doubleTheDonation.clearValue()
  }

  return (
    <Combobox
      fieldLabel={t('employerMatching.fieldLabel', 'Employer matching:')}
      placeholder={t('employerMatching.placeholder', 'Select a company')}
      defaultText={t('employerMatching.defaultText', 'Select a company')}
      selectedValue={selectedValue}
      selectedLabel={selectedLabel}
      options={data}
      loading={loading}
      onSelect={handleSelect}
      onSearchChange={handleSearchChange}
      onDropdownToggle={handleDropdownToggle}
      clearable={true}
      onClear={handleClear}
      searchQuery={searchQuery}
      ariaLabel={t('employerMatching.ariaLabel', 'Select employer for donation matching')}
      ariaOptionsLabel={t('employerMatching.ariaOptionsLabel', 'Employer matching options')}
      dataIdPrefix="employer-matching"
      noResultsText={t('employerMatching.noResults', 'No companies found for "{{query}}"', {
        query: searchQuery,
      })}
      searchPlaceholder={t('employerMatching.searchPlaceholder', 'Search for a company')}
    />
  )
}

export default EmployerMatching
